import { TFunction } from "i18next";
import { ChecklistGroup } from "./checklistGroups";
import { vehicleIdentification } from "./vehicleIdentification";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import VinDetail from "@/models/VinDetail";
import { exportFileName } from "@/utilities/exportFileName";

/**
 * The checklist as a sheet.
 *
 * The vehicle is named first: the sheet is filed as the record of an installation, and a file
 * name gets lost. Below it the checks form a flat table with the group repeated on every row, so
 * the sheet can be sorted and filtered without losing which group a check belongs to.
 *
 * The last column says where the value came from. Without it a reading from yesterday looks
 * exactly like one reported a second ago, which is the one distinction the checklist exists for.
 */
export function buildChecklistExportRows(
  device: DeviceWithVehicleInfo | null | undefined,
  groups: ChecklistGroup[],
  t: TFunction,
  vin?: VinDetail,
): string[][] {
  const notFound = t("liveData.checklist.notFound", "not found");

  return [
    ...vehicleIdentification(device, t, notFound, vin).map(field => [field.label, field.value]),
    [],
    [
      t("liveData.checklist.exportGroup", "Group"),
      t("liveData.checklist.exportCheck", "Check"),
      t("liveData.value", "Value"),
      t("liveData.checklist.exportReported", "Reported"),
    ],
    ...groups.flatMap(group =>
      group.items.map(item => [
        group.title,
        item.label,
        item.value || notFound,
        reportedIn(item.found, item.fromFallback, t),
      ]),
    ),
  ];
}

function reportedIn(found: boolean, fromFallback: boolean, t: TFunction): string {
  if (!found) {
    return "";
  }
  return fromFallback
    ? t("liveData.checklist.reportedHistory", "Last 24 hours")
    : t("liveData.checklist.reportedLive", "Live");
}

/** A file name that says which vehicle was checked and when. */
export function checklistFileName(
  device: DeviceWithVehicleInfo | null | undefined,
  exportedAt: Date,
): string {
  return exportFileName("checklist", device?.name ?? device?.serialNumber ?? "", exportedAt);
}
